import { Icon } from '@openeducation/oe-rn-components';
import { ResizeMode } from 'expo-av';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, Platform, ScrollView, StyleSheet, View } from 'react-native';
import { IconButton } from 'react-native-paper';

import LessonRating from './lesson-rating';
import ReferralMessage from './referral-message';
import SectionCompleteFooter from './section-complete-footer';
import { useColors, useFont } from '../../../hooks';
import {
  useLessonContext,
  useStudentInfo,
} from '../../../hooks/lesson-player.context';
import { useActivityContext } from '../../../hooks/use-activity.context';
import { useLessonSettings } from '../../../hooks/use-lesson-settings';
import openURL from '../../../utils/open-url';
import LessonAccentBanner from '../../lesson-accent-banner/lesson-accent-banner';
import ThemedText2 from '../../themed-text-2';
import { ThemedView } from '../../themed-view';
import { Card } from '../card/card';
import { CardHeader } from '../card/card-header/card-header';

const SectionCompleteDetailed: React.FC = () => {
  const fonts = useFont();
  const { t } = useTranslation();
  const colors = useColors();
  const { isCurrentActivityComplete, lessonInfo, redirectToReferralPage } =
    useActivityContext();

  const studentInfo = useStudentInfo();
  const { isFooterVisible, setFooterVisibility } = useLessonSettings();
  const { referralMessageElligible } = useLessonContext();
  const { referralBonusAmount } = useLessonContext();
  const initialFooterVisibilityRef = useRef(isFooterVisible);

  useEffect(() => {
    const initialFooterVisibility = initialFooterVisibilityRef.current;
    setFooterVisibility(false);
    return () => {
      setFooterVisibility(initialFooterVisibility);
    };
  }, [setFooterVisibility]);

  if (!studentInfo || !lessonInfo) {
    return null;
  }

  return (
    <>
      <ScrollView contentContainerStyle={styles.scrollContainer}>
        <LessonAccentBanner
          style={styles.banner}
          accentStyle={styles.bannerAccent}
        >
          <View style={styles.confettiContainer}>
            {Platform.OS === 'web' ? (
              <View style={styles.confettiWebContainer}>
                <Image
                  resizeMode={ResizeMode.COVER}
                  style={styles.confettiSmall}
                  source={require('../../../assets/images/confetti-left.png')}
                />
                <View style={styles.confettiSpacer} />
                <Image
                  resizeMode={ResizeMode.COVER}
                  style={styles.confettiSmall}
                  source={require('../../../assets/images/confetti-right.png')}
                />
              </View>
            ) : (
              <Image
                resizeMode={ResizeMode.COVER}
                style={styles.confetti}
                source={require('../../../assets/images/confetti.png')}
              />
            )}
          </View>
          <ThemedText2
            type="onPrimary"
            style={[styles.congratsTitle, fonts.primary.bold]}
          >
            {t('sectionComplete.title')}
          </ThemedText2>
          <View style={styles.profileContainer}>
            {studentInfo.image && (
              <Image
                resizeMode={ResizeMode.COVER}
                style={[
                  styles.profileImage,
                  { borderColor: colors.background },
                ]}
                source={{ uri: studentInfo.image }}
              />
            )}
            <ThemedText2
              type="onPrimary"
              numberOfLines={1}
              style={[styles.studentName, fonts.primary.bold]}
            >
              {studentInfo.name}
            </ThemedText2>
          </View>
          <Card containerStyle={styles.card} solid>
            <CardHeader>
              <View style={styles.cardHeaderContent}>
                <ThemedText2
                  style={[
                    styles.cardHeaderText,
                    { color: colors.lightSilverGray },
                  ]}
                >
                  {t('sectionComplete.subtitle')}:
                </ThemedText2>
              </View>
            </CardHeader>
            <View style={styles.cardContent}>
              <ThemedText2 style={[styles.lessonTitle, fonts.primary.bold]}>
                {lessonInfo.title}
              </ThemedText2>
              <View style={styles.subtitleContainer}>
                {!!lessonInfo.subtitle && (
                  <ThemedView
                    type="primaryLight"
                    style={styles.lessonSubtitleView}
                  >
                    <ThemedText2
                      style={[
                        styles.lessonSubtitle,
                        { backgroundColor: colors.primaryLight },
                      ]}
                    >
                      {lessonInfo.subtitle}
                    </ThemedText2>
                  </ThemedView>
                )}
                {!!lessonInfo.titleType && (
                  <ThemedText2 type="primary" style={styles.lessonSubtitle}>
                    {lessonInfo.titleType}
                  </ThemedText2>
                )}
              </View>
              <ThemedText2>{lessonInfo.description}</ThemedText2>
            </View>
            <ThemedView type="background" style={styles.socialMediaContainer}>
              <IconButton
                size={16}
                hitSlop={{ top: 12, right: 12, bottom: 12, left: 12 }}
                icon={({ size }) => (
                  <Icon name="twitter" {...{ size }} type="brand" />
                )}
                onPress={() =>
                  openURL(
                    `https://twitter.com/intent/tweet?text=${t(
                      'sectionComplete.twitterMessage'
                    )}`
                  )
                }
              />
            </ThemedView>
          </Card>
        </LessonAccentBanner>
        {!!lessonInfo.id && (
          <View style={styles.ratingContainer}>
            <LessonRating lessonId={lessonInfo.id} personId={studentInfo.id} />
          </View>
        )}
        {referralMessageElligible && (
          <ReferralMessage
            referralBonusAmount={referralBonusAmount}
            onActionPress={() => redirectToReferralPage()}
          />
        )}
      </ScrollView>
      {isCurrentActivityComplete && (
        <SectionCompleteFooter
          extraButtonType={lessonInfo.nextLessonButtonType}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  scrollContainer: {
    paddingBottom: 25,
  },
  confettiContainer: {
    position: 'absolute',
    top: -25,
    left: 0,
    right: 0,
    bottom: 25,
    alignItems: 'center',
  },
  confettiWebContainer: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  confetti: {
    width: 637,
    height: 227,
  },
  confettiSmall: {
    width: 163,
    height: 227,
  },
  confettiSpacer: {
    width: 500,
  },
  banner: {
    paddingTop: 30,
  },
  bannerAccent: {
    maxHeight: 175,
  },
  congratsTitle: {
    fontSize: Platform.select({
      web: 32,
      default: 24,
    }),
    textAlign: 'center',
  },
  profileContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginVertical: 25,
    paddingHorizontal: 35,
  },
  profileImage: {
    width: 24,
    height: 24,
    borderRadius: 24,
    marginRight: 8,
    borderWidth: 1,
  },
  studentName: {
    fontSize: 16,
  },
  card: {
    maxWidth: '100%',
    borderRadius: 12,
    alignSelf: 'center',
    width: 500,
  },
  cardHeaderContent: {
    paddingVertical: 8,
    paddingHorizontal: 20,
  },
  cardContent: {
    padding: 20,
  },
  cardHeaderText: {
    fontSize: 12,
  },
  ratingContainer: {
    marginTop: 0,
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  lessonTitle: {
    fontSize: 18,
    marginBottom: 8,
  },
  subtitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  lessonSubtitleView: {
    paddingVertical: 2,
    paddingHorizontal: 4,
    borderRadius: 4,
    marginRight: 12,
  },
  lessonSubtitle: {
    fontSize: 13,
  },
  socialMediaContainer: {
    flexDirection: 'row',
    position: 'absolute',
    bottom: -18,
    alignSelf: 'center',
  },
});

export default SectionCompleteDetailed;
